import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErr } from "@/lib/format";
import {
  PaperclipIcon, TrashIcon, UploadSimpleIcon, FileTextIcon,
  FilePdfIcon, FileXlsIcon, FileImageIcon, FileIcon, SpinnerIcon, LinkIcon,
  WarningIcon
} from "@phosphor-icons/react";

/**
 * Centro de documentos universal — adjuntos para cualquier entidad.
 *
 * Soporta DOS modos:
 *  1. Subida DIRECTA de archivo a Cloudflare R2 → /api/uploads
 *  2. Pegar una URL externa (Drive, Dropbox, etc.)
 *
 * El metadata del adjunto siempre se persiste en MongoDB → /api/attachments/{type}/{id}
 *
 * Uso:
 *   <DocumentCenter entityType="invoice" entityId={inv.id} />
 *   <DocumentCenter entityType="client" entityId={cliente.id} folder="clients" />
 *
 * entityType permitido: client | product | supplier | purchase_order | invoice |
 *                       purchase_invoice | expense | payment
 */
const ENTITY_TO_FOLDER = {
  client: "clients",
  product: "products",
  supplier: "suppliers",
  purchase_order: "purchase-orders",
  invoice: "invoices",
  purchase_invoice: "purchase-invoices",
  expense: "expenses",
  payment: "payments",
};

const TIPO_OPTS = [
  { value: "documento", label: "Documento" },
  { value: "imagen", label: "Imagen" },
  { value: "escaneo", label: "Escaneo" },
  { value: "factura", label: "Factura" },
  { value: "contrato", label: "Contrato" },
  { value: "comprobante", label: "Comprobante" },
  { value: "otro", label: "Otro" },
];

function _iconForMime(mime) {
  if (!mime) return FileTextIcon;
  if (mime.startsWith("image/")) return FileImageIcon;
  if (mime === "application/pdf") return FilePdfIcon;
  if (mime.includes("excel") || mime.includes("spreadsheet")) return FileXlsIcon;
  return FileIcon;
}

function _tipoFromMime(mime, filename = "") {
  if (!mime) return "documento";
  if (mime.startsWith("image/")) return "imagen";
  if (mime === "application/pdf") {
    const fn = filename.toLowerCase();
    if (fn.includes("factura")) return "factura";
    if (fn.includes("contrato")) return "contrato";
    if (fn.includes("comprobante") || fn.includes("recibo")) return "comprobante";
    return "documento";
  }
  return "documento";
}

export default function DocumentCenter({
  entityType,
  entityId,
  compact = false,
  title = "Centro de documentos",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showUrlForm, setShowUrlForm] = useState(false);
  const [urlForm, setUrlForm] = useState({ url: "", nombre: "", descripcion: "", tipo: "documento" });
  const [saving, setSaving] = useState(false);
  const [r2Enabled, setR2Enabled] = useState(true);
  const fileRef = useRef(null);

  const folder = ENTITY_TO_FOLDER[entityType] || "documents";

  const load = async () => {
    if (!entityType || !entityId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/attachments/${entityType}/${entityId}`);
      setItems(data || []);
    } catch (e) { /* silenciar: entidad nueva sin adjuntos */ }
    finally { setLoading(false); }
  };

  // Verificar si R2 está habilitado
  useEffect(() => {
    api.get("/uploads/config")
      .then(({ data }) => setR2Enabled(!!data?.enabled))
      .catch(() => setR2Enabled(false));
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityType, entityId]);

  // === SUBIDA DIRECTA DE ARCHIVO ===
  const handleFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of list) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`"${file.name}" supera 10 MB`);
        continue;
      }
      try {
        // 1) Subir a R2
        const fd = new FormData();
        fd.append("file", file);
        fd.append("folder", folder);
        fd.append("kind", "any");
        const { data: upload } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
          },
        });
        // 2) Asociar como attachment
        await api.post(`/attachments/${entityType}/${entityId}`, {
          url: upload.url,
          nombre: file.name,
          descripcion: "",
          tipo: _tipoFromMime(file.type, file.name),
          tamano_bytes: file.size,
        });
        ok++;
      } catch (err) {
        toast.error(`Error con "${file.name}": ${apiErr(err)}`);
      }
    }
    if (ok > 0) toast.success(`${ok} archivo(s) subido(s)`);
    setUploading(false);
    setProgress(0);
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  // === ENLACE EXTERNO ===
  const addUrl = async (e) => {
    e?.preventDefault?.();
    if (!urlForm.url) { toast.error("URL requerida"); return; }
    setSaving(true);
    try {
      await api.post(`/attachments/${entityType}/${entityId}`, urlForm);
      toast.success("Enlace agregado");
      setUrlForm({ url: "", nombre: "", descripcion: "", tipo: "documento" });
      setShowUrlForm(false);
      load();
    } catch (er) { toast.error(apiErr(er)); }
    finally { setSaving(false); }
  };

  const remove = async (att) => {
    if (!window.confirm(`¿Eliminar adjunto "${att.nombre || att.url}"?`)) return;
    try {
      await api.delete(`/attachments/${entityType}/${entityId}/${att.id}`);
      toast.success("Adjunto eliminado"); load();
    } catch (er) { toast.error(apiErr(er)); }
  };

  return (
    <div className={`bg-card border border-border/60 rounded-lg ${compact ? "p-3" : "p-4"}`} data-testid={`doc-center-${entityType}`}>
      <div className="flex items-center gap-2 mb-3">
        <PaperclipIcon size={16} className="text-agro-yellow" weight="duotone" />
        <h3 className="text-sm font-semibold flex-1">{title}</h3>
        <span className="text-[10px] text-text-muted">{items.length} archivo{items.length === 1 ? "" : "s"}</span>
      </div>

      {/* Botones de acción */}
      <div className="flex flex-wrap gap-2 mb-3">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || !r2Enabled}
          data-testid={`doc-upload-${entityType}`}
          className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-agro-green/40 bg-agro-green/10 text-agro-green rounded-md hover:bg-agro-green/20 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? (
            <><SpinnerIcon size={14} className="animate-spin" /> Subiendo {progress > 0 ? `${progress}%` : "…"}</>
          ) : (
            <><UploadSimpleIcon size={14} weight="bold" /> Subir archivo</>
          )}
        </button>
        {!r2Enabled && (
          <span className="text-[10px] text-amber-400 self-center">
            <WarningIcon size={14} className="inline align-[-2px]" aria-hidden="true" /> Subida directa deshabilitada (R2 no configurado)
          </span>
        )}
        <button
          type="button"
          onClick={() => setShowUrlForm((v) => !v)}
          data-testid={`doc-url-toggle-${entityType}`}
          className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-border rounded-md hover:border-agro-green/40 hover:bg-white/5"
        >
          <LinkIcon size={14} weight="bold" /> {showUrlForm ? "Cancelar enlace" : "Agregar enlace externo"}
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          onChange={handleFiles}
          className="hidden"
          data-testid={`doc-file-input-${entityType}`}
        />
      </div>

      {/* Formulario de URL externa (oculto por defecto) */}
      {showUrlForm && (
        <form onSubmit={addUrl} className="grid grid-cols-1 sm:grid-cols-6 gap-2 mb-3 p-3 bg-app/40 border border-border/40 rounded-md">
          <input
            value={urlForm.url}
            onChange={(e) => setUrlForm({ ...urlForm, url: e.target.value })}
            placeholder="https://drive.google.com/..."
            data-testid={`doc-url-input-${entityType}`}
            className="sm:col-span-3 bg-app border border-border rounded-md px-3 py-2 text-xs focus:border-agro-green"
            required
          />
          <input
            value={urlForm.nombre}
            onChange={(e) => setUrlForm({ ...urlForm, nombre: e.target.value })}
            placeholder="Nombre (opcional)"
            className="bg-app border border-border rounded-md px-3 py-2 text-xs focus:border-agro-green"
          />
          <select
            value={urlForm.tipo}
            onChange={(e) => setUrlForm({ ...urlForm, tipo: e.target.value })}
            className="bg-app border border-border rounded-md px-3 py-2 text-xs focus:border-agro-green"
          >
            {TIPO_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button
            type="submit"
            disabled={saving}
            data-testid={`doc-url-add-${entityType}`}
            className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-3 py-2 text-xs flex items-center justify-center gap-1 disabled:opacity-50"
          >
            <LinkIcon size={14} weight="bold" /> {saving ? "Guardando…" : "Agregar"}
          </button>
        </form>
      )}

      {/* Lista de adjuntos */}
      {loading ? (
        <div className="text-xs text-text-muted text-center py-3">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-text-muted text-center py-6 border border-dashed border-border/40 rounded-md">
          Sin documentos adjuntos. Subí archivos o agregá enlaces externos.
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((a) => {
            const Icon = _iconForMime(a.content_type) || FileTextIcon;
            const isInternal = !!a.url && a.url.includes("r2.dev");
            const sizeKB = a.tamano_bytes ? `${(a.tamano_bytes / 1024).toFixed(0)} KB` : "";
            return (
              <div
                key={a.id}
                data-testid={`doc-item-${a.id}`}
                className="flex items-center gap-3 px-3 py-2 bg-app/40 border border-border/40 rounded-md hover:border-agro-green/40 transition-colors"
              >
                <Icon size={18} className="text-agro-green flex-shrink-0" weight="duotone" />
                <a
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 min-w-0 text-xs hover:text-agro-green truncate font-medium"
                  title={a.url}
                >
                  {a.nombre || a.url}
                </a>
                <span className="text-[10px] text-text-muted uppercase whitespace-nowrap">{a.tipo}</span>
                {sizeKB && <span className="text-[10px] text-text-muted whitespace-nowrap">{sizeKB}</span>}
                {!isInternal && (
                  <span className="text-[9px] text-amber-400/80 uppercase tracking-wider" title="Enlace externo">URL</span>
                )}
                <button
                  onClick={() => remove(a)}
                  title="Eliminar"
                  className="p-1 text-text-muted hover:text-red-400"
                  data-testid={`doc-del-${a.id}`}
                >
                  <TrashIcon size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
